import {
  Component,
  ElementRef,
  Injector,
  NgZone,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatLegacySnackBar as MatSnackBar, MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PaneComments,
  guardColleagueAnnotations,
  withLockFlags,
} from '@app/app/page-modules/pdf-annotation-editor/helpers/colleague-annotations.util';
import { isReply } from '@app/app/page-modules/pdf-annotation-editor/helpers/pdf-comment-threads.util';
import {
  AnnotationTarget,
  AnnotationsWithDraft,
  PdfAnnotationFocus,
  ShownPdfAnnotations,
} from '@app/app/page-modules/pdf-annotation-editor/models/pdf-annotation.model';
import { PDF_ANNOTATION_DATA_SERVICE } from '@app/app/page-modules/pdf-annotation-editor/services/pdf-annotation.service';
import { SnackbarHelper } from '@app/app/shared/helpers/snackbar-helper';
import { requestErrorToString, userFacingRequestError } from '@app/app/shared/utils/error-handling.utils';
import { linkedSignal } from '@app/app/signal-backports/linked_signal';
import { resource } from '@app/app/signal-backports/resource';
import { ResourceRef } from '@app/app/signal-backports/resource_api';
import { rxResource } from '@app/app/signal-backports/rx_resource';
import {
  PdfAnnotationObject,
  PdfAnnotationSubtype,
  PdfDocumentObject,
  PdfErrorReason,
  TaskAbortedError,
  TaskRejectedError,
} from '@embedpdf/models';
import { TrackedAnnotation } from '@embedpdf/plugin-annotation';
import EmbedPDF, {
  AnnotationCapability,
  AnnotationEvent,
  AnnotationPlugin,
  DocumentManagerCapability,
  DocumentManagerPlugin,
  EmbedPdfContainer,
  GroupItem,
  PluginRegistry,
  ScrollPlugin,
  ToolbarItem,
  UICapability,
  UIPlugin,
  UISchema,
  ZoomMode,
  version as embedPdfVersion,
} from '@embedpdf/snippet';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { AlertSquareIcon } from '@hugeicons/core-free-icons/index';
import { TranslocoService } from '@ngneat/transloco';
import { isEqual } from 'lodash-es';
import { EMPTY, Observable, Subject, debounce, defer, of, throwError, timer } from 'rxjs';
import { catchError, filter, finalize, map, skip, switchMap, take, tap } from 'rxjs/operators';

/** Recorded on each save, so annotations stored by one editor version can be migrated when a later one changes them. */
const ENGINE_VERSION = `embedpdf@${embedPdfVersion}`;

/** What an annotation's author is called when the viewer may not know who drew it. */
const HIDDEN_AUTHOR_KEYS: Record<AnnotationTarget['type'], string> = {
  assignmentTaskForm: 'translations.v3Forms.fileUpload.pdfComment.hiddenAuthor.marker',
  learnerForm: 'translations.v3Forms.fileUpload.pdfComment.hiddenAuthor.reviewer',
};

@Component({
  selector: 'ptl-embedpdf-annotation-editor',
  standalone: true,
  // The snack bar is scoped to its module, and a host may not have it.
  imports: [HugeiconsIconComponent, MatProgressSpinnerModule, MatButtonModule, MatSnackBarModule],
  templateUrl: './embedpdf-annotation-editor.component.html',
  styleUrl: './embedpdf-annotation-editor.component.scss',
})
export class EmbedpdfAnnotationEditorComponent {
  private annotationService = inject(PDF_ANNOTATION_DATA_SERVICE);
  private transloco = inject(TranslocoService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private injector = inject(Injector);
  private ngZone = inject(NgZone);
  private snackBar = inject(MatSnackBar);

  pdfUrl = input.required<string | undefined>();
  annotationTarget = input.required<AnnotationTarget | undefined>();
  readOnly = input(false);
  commentAuthorName = input<string | undefined>(undefined);
  /** An annotation to bring into view and select, e.g. from its comment listed beside the PDF. A new object asks again. */
  focusedAnnotation = input<PdfAnnotationFocus | undefined>(undefined);
  /** Raised by the host each time it sends feedback, or a thread list beside the PDF changes a comment. */
  feedbackRevision = input(0);

  pdfLoadingState = output<PdfLoadingState>();
  annotationLoadingState = output<PdfLoadingState>();
  annotationSavingStateOutput = output<AnnotationSavingState | undefined>();
  /** The annotations on show once loaded, and again after each save: the committed ones for a reader, the draft for an editor. */
  annotationsChange = output<ShownPdfAnnotations>();
  /** A reply written, edited or deleted in the comment pane reached the server, as a feedback comment. */
  commentSaved = output<void>();

  protected showSpinner = computed(
    () =>
      (!this.pdfDocument.value() ||
        (this.annotationTarget() && (!this.initialAnnotations.value() || !this.hasAppliedInitialAnnotations()))) &&
      !this.hasError(),
  );
  protected hasError = computed(() => this.pdfDocument.error() || this.initialAnnotations.error());
  protected viewerHidden = computed(() => this.showSpinner() || this.hasError());
  protected pdfDocumentErrorMessage = computed(() => {
    const error = this.pdfDocument.error() as Error;
    if (!error) return undefined;

    return 'PDF loading error: ' + error.toString();
  });
  protected initialAnnotationsErrorMessage = computed(() => {
    const error = this.initialAnnotations.error();
    if (!error) return undefined;

    return 'Annotation loading error: ' + requestErrorToString(error);
  });
  protected secondsUntilRetry = signal<number | undefined>(undefined);
  protected annotationSavingState = linkedSignal<AnnotationSavingState | undefined>(() => {
    this.pdfDocument.value(); // reset on document load
    return undefined;
  });

  private saveRetryTimer: ReturnType<typeof setTimeout> | null = null;

  private pdfViewerContainer = viewChild<ElementRef<HTMLDivElement>>('pdfViewerContainer');
  private embedPdfContainer = signal<EmbedPdfContainer | undefined>(undefined);

  private pluginRegistry: ResourceRef<PluginRegistry> = resource({
    request: () => this.embedPdfContainer(),
    loader: async ({ request: embedPdfContainer }) => {
      const registry = await embedPdfContainer.registry;
      await registry.pluginsReady();
      return registry;
    },
  });
  private pdfDocument: ResourceRef<PdfDocumentObject> = resource({
    request: () => ({
      pdfUrl: this.pdfUrl()?.replaceAll('pdf', 'pdf'),
      pluginsRegistry: this.pluginRegistry.value(),
      readOnly: this.readOnly(),
    }),
    loader: async ({ request: { pdfUrl, pluginsRegistry, readOnly } }) => {
      if (!pdfUrl || !pluginsRegistry) return undefined;

      const documentManager: DocumentManagerCapability = pluginsRegistry
        .getPlugin<DocumentManagerPlugin>(DocumentManagerPlugin.id)
        ?.provides();
      if (!documentManager) return undefined;

      try {
        await documentManager.closeAllDocuments().toPromise();
      } catch (_e) {
        // Don't care about failed close
      }

      this.cancelSaveRetry();

      const openTask = documentManager.openDocumentUrl({ url: pdfUrl, permissions: { overrides: { modifyAnnotations: !readOnly } } });
      const { task: loadTask } = await openTask.toPromise();
      return await loadTask.toPromise();
    },
  });
  private initialAnnotations = rxResource({
    request: () => ({ annotationTarget: this.annotationTarget(), readOnly: this.readOnly() || !this.annotationTarget() }),
    loader: ({ request: { annotationTarget, readOnly } }) => {
      if (!annotationTarget) return of(undefined);
      return this.annotationService.loadAnnotations(annotationTarget, !readOnly).pipe(
        // A reader can open a file nothing was ever annotated against — a learner's upload made after their work was
        // returned is in no submitted cycle. That is no annotations to show, not a broken document.
        catchError((error: unknown) =>
          readOnly && (error as { status?: number } | null)?.status === 404 ? of<AnnotationsWithDraft>({}) : throwError(() => error),
        ),
        map((loaded) => this.withHiddenAuthorsNamed(loaded, annotationTarget)),
      );
    },
  });
  private hasAppliedInitialAnnotations = linkedSignal({
    source: () => [this.annotationTarget(), this.pdfDocument.value()],
    computation: () => false,
  });
  /** On an assignment PDF a reply is a feedback comment, served as an annotation; on a playlist's it is an annotation. */
  private repliesAreComments = computed(() => this.annotationTarget()?.type === 'assignmentTaskForm');
  private annotationSavingState$ = toObservable(this.annotationSavingState);
  /** Whether a selected annotation is a colleague's, so the buttons that would change it are hidden. */
  private colleagueAnnotationSelected = signal(false);
  /** The document whose pages the viewer has laid out. A scroll asked for before then is lost. */
  private laidOutDocumentId = signal<string | undefined>(undefined);
  /** What the server last confirmed it holds. A failed or cancelled save never lands here, so its retry sends again. */
  private lastSaveData: object[] | null = null;
  private lastSaveTime: Date | null = null;
  private savesDone = 0;
  /** The pane's replies posted, edited or deleted, and those not answered yet: a read overlapping one may predate it. */
  private commentWritesStarted = 0;
  private commentWritesPending = 0;

  private annotationSaveTriggers$ = new Subject<AnnotationSaveTrigger>();

  private readonly BASE_DISABLED_CATEGORIES = [
    'redaction',
    'document-print',
    'document-export',
    'document-open',
    'document-close',
    'document-screenshot',
    'document-capture',
    'document-protect',
    'rotate',
    // Stamps, signatures, images, attachments and form fields.
    'stamp',
    'insert',
    'form',
    'annotation-widget-edit',
    'annotation-redaction',
    // These create CARET and LINK annotations, which we don't save.
    'annotation-insert-text',
    'annotation-replace-text',
    'annotation-link',
    // Sticky-note tool added in 2.8; off to keep the toolbar as it was.
    'annotation-comment-tool',
  ];

  private readonly READONLY_DISABLED_CATEGORIES = [
    'annotation-tools-menu',
    'annotation-delete',
    'annotation-markup',
    'panel-annotation-style',
    'shapes-tools-menu',
    'mode-annotate',
    'mode-shapes',
    'mode-view',
  ];

  /** The selection menu's buttons that change an annotation, which the guard would refuse on a colleague's. */
  private readonly COLLEAGUE_ANNOTATION_DISABLED_CATEGORIES = [
    'annotation-style',
    'panel-annotation-style',
    'annotation-delete',
    'annotation-group',
  ];

  private readonly ANNOTATION_SUBTYPES_FOR_SAVE = [
    PdfAnnotationSubtype.HIGHLIGHT,
    PdfAnnotationSubtype.TEXT,
    PdfAnnotationSubtype.STRIKEOUT,
    PdfAnnotationSubtype.UNDERLINE,
    PdfAnnotationSubtype.INK,
    PdfAnnotationSubtype.FREETEXT,
    PdfAnnotationSubtype.LINE,
    PdfAnnotationSubtype.SQUARE,
    PdfAnnotationSubtype.CIRCLE,
    PdfAnnotationSubtype.POLYGON,
    PdfAnnotationSubtype.POLYLINE,
  ];

  private readonly AUTO_SAVE_DEBOUNCE_TIME = 1000;

  private readonly MANUAL_SAVE_DEBOUNCE_TIME = 200;

  private readonly SAVE_RETRY_WAIT_TIME = 3;

  constructor() {
    this._initEmbedPDFWhenReady();
    this._applyInitialAnnotationsWhenReady();
    this._triggerAutosaveOnAnnotationChange();
    this._debouncedSaveOnTrigger();
    this._protectColleagueAnnotations();
    this._syncDisabledCategories();
    this._moveFullscreenButtonToMainToolbar();
    this._outputPdfLoadingStateOnChange();
    this._outputAnnotationLoadingStateOnChange();
    this._outputSavingStateOnChange();
    this._outputAnnotationsOnChange();
    this._trackLayout();
    this._scrollIntoViewOnFocus();
    this._showFocusedAnnotation();
    this._rereadFeedbackOnRevision();
  }

  private _initEmbedPDFWhenReady() {
    effect(() => {
      if (this.embedPdfContainer()) return;
      const element = this.pdfViewerContainer()?.nativeElement;
      if (!element) return;

      const container = EmbedPDF.init({
        target: this.pdfViewerContainer().nativeElement,
        type: 'container',
        documentManager: {
          maxDocuments: 1,
        },
        theme: {
          preference: 'light',
        },
        tabBar: 'never',
        // The snippet defaults to fit-page, which shrinks the text to unreadable in a short viewer.
        zoom: {
          defaultZoomLevel: ZoomMode.FitWidth,
        },
        disabledCategories: [...this.BASE_DISABLED_CATEGORIES],
        permissions: {
          overrides: {
            copyContents: true,
            modifyAnnotations: true,
            print: false,
            modifyContents: false,
            fillForms: false,
            assembleDocument: false,
          },
        },
        annotations: {
          // currently annotation author can only be set up on init, no way to change it later - only first input value will take effect
          annotationAuthor: untracked(() => this.commentAuthorName()) || undefined,
        },
      });
      if (container) {
        untracked(() => this.embedPdfContainer.set(container));
      }
    });
  }

  private _applyInitialAnnotationsWhenReady() {
    effect(() => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      const initialAnnotations = this.initialAnnotations.value();
      if (!pluginRegistry || !document || !initialAnnotations || this.hasAppliedInitialAnnotations()) return;
      const annotation: AnnotationCapability = pluginRegistry.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
      if (!annotation) return;

      const toImport = this.annotationsToShow(initialAnnotations).map((shown) => (this.isEditable(shown) ? shown : withLockFlags(shown)));

      untracked(() => this.hasAppliedInitialAnnotations.set(true));
      if (toImport.length > 0) {
        console.log('Applying initial annotations', toImport);
        annotation.importAnnotations(toImport.map((x) => ({ annotation: x })));
      }
    });
  }

  private _triggerAutosaveOnAnnotationChange() {
    effect(async () => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      if (!pluginRegistry || !document || !this.hasAppliedInitialAnnotations()) return;

      const annotation: AnnotationCapability = pluginRegistry.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
      annotation.onAnnotationEvent((event) => {
        console.log('annotation event', event);
        if (event.type === 'loaded') return;
        // Such a reply is a feedback comment the server already has, and a save leaves it out.
        if (isReply(event.annotation) && this.repliesAreComments()) return;

        this.annotationSaveTriggers$.next({ type: 'annotationEvent', event, time: new Date() });
      });
    });
  }

  private _debouncedSaveOnTrigger() {
    this.annotationSaveTriggers$
      .pipe(
        filter(({ time }) => !this.lastSaveTime || this.lastSaveTime < time),
        tap(() => {
          this.annotationSavingState.set({ state: 'saving' });
          if (this.saveRetryTimer) {
            clearInterval(this.saveRetryTimer);
            this.saveRetryTimer = null;
            this.secondsUntilRetry.set(undefined);
          }
        }),
        debounce(({ type }) => {
          switch (type) {
            case 'annotationEvent':
              return timer(this.AUTO_SAVE_DEBOUNCE_TIME);
            case 'manualTrigger':
            case 'retry':
              return timer(this.MANUAL_SAVE_DEBOUNCE_TIME);
          }
        }),
        switchMap((trigger) => this.saveAnnotations(trigger)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /** Only the viewer's own annotations are theirs to change: see `guardColleagueAnnotations`. */
  private _protectColleagueAnnotations() {
    effect((onCleanup) => {
      const plugin = this.pluginRegistry.value()?.getPlugin<AnnotationPlugin>(AnnotationPlugin.id);
      if (!plugin) return;

      guardColleagueAnnotations(plugin, { editable: (id) => this.isEditable({ id }), comments: () => this.paneComments() });
      onCleanup(
        plugin.provides().onStateChange(({ state }) => {
          const selected = state.selectedUids.some((uid) => {
            const annotation = state.byUid[uid]?.object;
            return !!annotation && !this.isEditable(annotation);
          });
          untracked(() => this.colleagueAnnotationSelected.set(selected));
        }),
      );
    });
  }

  private _syncDisabledCategories() {
    effect(async () => {
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      if (!pluginRegistry || !document) return;

      const ui: UICapability = pluginRegistry.getPlugin<UIPlugin>(UIPlugin.id)?.provides();
      if (!ui) return;

      const disabled = new Set([
        ...(this.readOnly() ? this.READONLY_DISABLED_CATEGORIES : []),
        ...(this.colleagueAnnotationSelected() ? this.COLLEAGUE_ANNOTATION_DISABLED_CATEGORIES : []),
      ]);
      for (const c of new Set([...this.READONLY_DISABLED_CATEGORIES, ...this.COLLEAGUE_ANNOTATION_DISABLED_CATEGORIES])) {
        if (disabled.has(c)) {
          ui.disableCategory(c);
        } else {
          ui.enableCategory(c);
        }
      }
    });
  }

  private _moveFullscreenButtonToMainToolbar() {
    const documentFullscreenButtonId = 'document-fullscreen-button';

    effect(() => {
      const pluginRegistry = this.pluginRegistry.value();
      if (!pluginRegistry) return;

      const ui: UICapability = pluginRegistry.getPlugin<UIPlugin>(UIPlugin.id)?.provides();
      if (!ui) return;

      const originalSchema: UISchema = ui.getSchema();
      const mainToolbarItems: ToolbarItem[] = [...originalSchema.toolbars['main-toolbar'].items];
      if (!mainToolbarItems) return;

      const leftGroupIdx = mainToolbarItems.findIndex((it) => it.id === 'left-group');
      if (leftGroupIdx < 0 || (mainToolbarItems[leftGroupIdx].items as GroupItem[]).some((it) => it.id === documentFullscreenButtonId)) {
        return;
      }
      const leftGroup: GroupItem = JSON.parse(JSON.stringify(mainToolbarItems[leftGroupIdx]));
      mainToolbarItems[leftGroupIdx] = leftGroup;

      const documentMenuButtonIdx = leftGroup.items.findIndex((it) => it.id === 'document-menu-button');
      if (documentMenuButtonIdx < 0) return;

      leftGroup.items.splice(documentMenuButtonIdx, 0, {
        type: 'command-button',
        id: documentFullscreenButtonId,
        commandId: 'document:fullscreen',
        variant: 'icon',
        categories: ['document', 'document-fullscreen'],
      });

      ui.mergeSchema({
        toolbars: {
          'main-toolbar': {
            items: mainToolbarItems,
          },
        },
      });
      ui.disableCategory('document-menu');
    });
  }

  private _outputPdfLoadingStateOnChange() {
    effect(() => {
      if (this.pdfDocument.isLoading()) {
        this.pdfLoadingState.emit({ state: 'loading' });
      } else if (this.pdfDocument.error()) {
        const error = this.pdfDocument.error();
        if (error instanceof TaskRejectedError || error instanceof TaskAbortedError) {
          this.pdfLoadingState.emit({
            state: 'failed',
            error: (error.reason as PdfErrorReason).message,
          });
        } else {
          this.pdfLoadingState.emit({
            state: 'failed',
            error: error.toString() || 'unknown error',
          });
        }
      } else if (this.pdfDocument.value()) {
        this.pdfLoadingState.emit({ state: 'loaded' });
      } else {
        this.pdfLoadingState.emit({ state: 'idle' });
      }
    });
  }

  private _outputAnnotationLoadingStateOnChange() {
    effect(() => {
      if (this.initialAnnotations.isLoading()) {
        this.annotationLoadingState.emit({ state: 'loading' });
      } else if (this.initialAnnotations.error()) {
        this.annotationLoadingState.emit({
          state: 'failed',
          error: requestErrorToString(this.initialAnnotations.error()),
        });
      } else if (this.initialAnnotations.value()) {
        this.annotationLoadingState.emit({ state: this.hasAppliedInitialAnnotations() ? 'loaded' : 'loading' });
      } else {
        this.annotationLoadingState.emit({ state: 'idle' });
      }
    });
  }

  private _outputSavingStateOnChange() {
    effect(() => this.annotationSavingStateOutput.emit(this.annotationSavingState()));
  }

  private _outputAnnotationsOnChange() {
    effect(() => {
      const loaded = this.initialAnnotations.value();
      if (loaded) this.annotationsChange.emit(this.shownAnnotations(loaded));
    });
  }

  private _trackLayout() {
    effect((onCleanup) => {
      const scroll = this.pluginRegistry.value()?.getPlugin<ScrollPlugin>(ScrollPlugin.id)?.provides();
      if (!scroll) return;

      onCleanup(scroll.onLayoutReady(({ documentId }) => untracked(() => this.laidOutDocumentId.set(documentId))));
    });
  }

  /** Brings the viewer on screen for a requested annotation, unless it is already there. */
  private _scrollIntoViewOnFocus() {
    effect(() => {
      if (!this.focusedAnnotation()) return;

      afterNextRender(
        () => {
          const element = this.host.nativeElement;
          const { top, bottom } = element.getBoundingClientRect();
          if (top < 0 || bottom > window.innerHeight) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        { injector: this.injector },
      );
    });
  }

  private _showFocusedAnnotation() {
    let shown: PdfAnnotationFocus | undefined;
    effect(() => {
      const request = this.focusedAnnotation();
      const pluginRegistry = this.pluginRegistry.value();
      const document = this.pdfDocument.value();
      if (!request || request === shown || !pluginRegistry || !document || !this.hasAppliedInitialAnnotations()) return;
      if (this.laidOutDocumentId() !== document.id) return;
      shown = request;

      untracked(() => {
        const annotation: AnnotationCapability = pluginRegistry.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
        const mark = annotation?.getAnnotationById(request.annotationId)?.object;
        const pageIndex = mark?.pageIndex ?? request.pageIndex;
        pluginRegistry
          .getPlugin<ScrollPlugin>(ScrollPlugin.id)
          ?.provides()
          // Where the viewer's own comment pane puts a mark it jumps to.
          .scrollToPage({ pageNumber: pageIndex + 1, pageCoordinates: mark && { ...mark.rect.origin }, alignX: 50, alignY: 25 });
        if (mark) annotation.selectAnnotation(pageIndex, mark.id);
      });
    });
  }

  /**
   * Sending feedback releases the drafts without changing them, so only their states are read again. Where replies are
   * feedback comments, the pane's replies and the marks' text are brought in step with the thread list too.
   */
  private _rereadFeedbackOnRevision() {
    toObservable(this.feedbackRevision)
      .pipe(
        skip(1),
        switchMap(() => {
          const target = this.annotationTarget();
          const syncThreads = this.repliesAreComments();
          const savesDone = this.savesDone;
          const commentWrites = this.commentWritesPending ? undefined : this.commentWritesStarted;
          if (!target || (this.readOnly() && !syncThreads)) return EMPTY;
          return this.annotationService.loadAnnotations(target, !this.readOnly()).pipe(
            map((loaded) => ({ target, syncThreads, savesDone, commentWrites, loaded: this.withHiddenAuthorsNamed(loaded, target) })),
            // A missed read only leaves a draft badge, or a reply, as it was until the page reloads.
            catchError(() => EMPTY),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ target, syncThreads, savesDone, commentWrites, loaded }) => {
        const current = this.initialAnnotations.value();
        if (!current || !isEqual(target, this.annotationTarget())) return;
        const served = this.annotationsToShow(loaded);
        // A save made while reading may be missing from the read, and is fresher than it.
        if (syncThreads && savesDone === this.savesDone) {
          this.syncMarks(this.annotationsToShow(current), served);
          this.initialAnnotations.set({ ...current, ...loaded });
        } else {
          this.initialAnnotations.set({ ...current, annotationDetails: loaded.annotationDetails });
        }
        // Likewise a reply the viewer wrote meanwhile: the read could undo it on screen though the server has it.
        if (syncThreads && commentWrites === this.commentWritesStarted) this.syncReplies(served);
      });
  }

  /**
   * Takes the marks' text as served where it changed since the last read or save, as a note edited or deleted in the
   * thread list does. A text box whose note was deleted is gone. The viewer's unsaved changes are left alone.
   */
  private syncMarks(known: object[], served: object[]) {
    const annotation: AnnotationCapability = this.pluginRegistry.value()?.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
    if (!annotation || !this.hasAppliedInitialAnnotations()) return;

    const marks = (annotations: object[]) =>
      new Map((annotations as PdfAnnotationObject[]).filter((mark) => !isReply(mark)).map((mark) => [mark.id, mark]));
    const servedMarks = marks(served);
    for (const [id, was] of marks(known)) {
      const shown = annotation.getAnnotationById(id);
      if (!shown || shown.commitState === 'deleted') continue;
      const fresh = servedMarks.get(id);
      if (!fresh) {
        if (annotation.getSelectedAnnotationIds().includes(id)) annotation.deselectAnnotation();
        annotation.purgeAnnotation(shown.object.pageIndex, id);
      } else if (fresh.contents !== was.contents) {
        annotation.syncAnnotationObject(id, { contents: fresh.contents, modified: fresh.modified });
      }
    }
  }

  /** Adds, changes and drops the pane's replies to match those served, leaving the marks alone. */
  private syncReplies(served: object[]) {
    const annotation: AnnotationCapability = this.pluginRegistry.value()?.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
    if (!annotation || !this.hasAppliedInitialAnnotations()) return;

    const servedReplies = new Map((served as PdfAnnotationObject[]).filter(isReply).map((reply) => [reply.id, reply]));
    const shown = Object.values<TrackedAnnotation>(annotation.getState().byUid)
      .filter(({ commitState }) => commitState !== 'deleted')
      .map(({ object }) => object)
      .filter(isReply);
    for (const reply of shown) {
      const fresh = servedReplies.get(reply.id);
      if (!fresh) {
        annotation.purgeAnnotation(reply.pageIndex, reply.id);
      } else if (fresh.contents !== reply.contents) {
        annotation.syncAnnotationObject(reply.id, { contents: fresh.contents, modified: fresh.modified });
      }
    }
    const shownIds = new Set(shown.map(({ id }) => id));
    const added = [...servedReplies.values()].filter(
      ({ id, inReplyToId }) => !shownIds.has(id) && annotation.getAnnotationById(inReplyToId),
    );
    if (added.length) {
      annotation.importAnnotations(
        added.map((reply) => ({ annotation: (this.isEditable(reply) ? reply : withLockFlags(reply)) as PdfAnnotationObject })),
      );
    }
  }

  /** Where the pane's replies go on an assignment PDF the viewer may annotate. */
  private paneComments(): PaneComments | undefined {
    const target = untracked(() => this.annotationTarget());
    if (target?.type !== 'assignmentTaskForm' || untracked(() => this.readOnly())) return undefined;
    const counted = <T>(write: Observable<T>) =>
      defer(() => {
        this.commentWritesStarted++;
        this.commentWritesPending++;
        return write.pipe(finalize(() => this.commentWritesPending--));
      });
    return {
      // A reply to a mark drawn moments ago waits for the mark's save: the server takes comments on saved marks only.
      post: (mark, contents) =>
        counted(
          this.annotationSavingState$.pipe(
            filter((saving) => saving?.state !== 'saving'),
            take(1),
            switchMap(() => this.annotationService.postAnnotationComment(target, mark.id, contents)),
          ),
        ),
      edit: (commentId, contents) => counted(this.annotationService.editAnnotationComment(target, commentId, contents)),
      delete: (commentId) => counted(this.annotationService.deleteAnnotationComment(target, commentId)),
      author: untracked(() => this.commentAuthorName()),
      saved: () => this.commentSaved.emit(),
      failed: (error) =>
        SnackbarHelper.showSnackBar(
          this.ngZone,
          this.snackBar,
          userFacingRequestError(error, this.transloco.translate('translations.v3Forms.fileUpload.pdfComment.notSaved')),
        ),
    };
  }

  private annotationsToShow({ committed, draft }: AnnotationsWithDraft): object[] {
    return (this.readOnly() ? committed : (draft ?? committed)) ?? [];
  }

  /** A reader is served only sent annotations. One drawn since loading has no details yet, and is an unsent draft. */
  private shownAnnotations(loaded: AnnotationsWithDraft): ShownPdfAnnotations {
    const annotations = this.annotationsToShow(loaded);
    const details = loaded.annotationDetails;
    const draftIds =
      this.readOnly() || !details
        ? []
        : annotations.map((annotation) => (annotation as { id: string }).id).filter((id) => details[id]?.state !== 'RELEASED');
    return { annotations, draftIds: new Set(draftIds) };
  }

  /** Whether the viewer drew the annotation. One drawn since loading has no details, and is theirs. */
  private isEditable(annotation: object): boolean {
    const { id } = annotation as { id?: string };
    return !id || untracked(() => this.initialAnnotations.value()?.annotationDetails?.[id]?.editable) !== false;
  }

  /**
   * An author the viewer may not know is served without a name, which the comment pane would show as "Guest" and the
   * feedback list as "?". They go by their role instead.
   */
  private withHiddenAuthorsNamed(loaded: AnnotationsWithDraft, target: AnnotationTarget): AnnotationsWithDraft {
    const role = this.transloco.translate(HIDDEN_AUTHOR_KEYS[target.type]);
    const named = (annotations: object[] | undefined) =>
      annotations?.map((annotation) => ((annotation as { author?: string }).author ? annotation : { ...annotation, author: role }));
    return { ...loaded, committed: named(loaded.committed), draft: named(loaded.draft) };
  }

  private saveAnnotations(trigger: AnnotationSaveTrigger): Observable<void> {
    const currentPdfUrl = this.pdfUrl();
    const currentTarget = this.annotationTarget();
    if (!currentPdfUrl || !currentTarget || this.readOnly() || !this.pdfDocument.value() || !this.hasAppliedInitialAnnotations()) {
      return of(void 0);
    }

    const annotations = this.getCurrentAnnotations();
    // A save replaces the saver's annotations on the server, and an id it doesn't know becomes a new one of theirs:
    // sending a colleague's that was deleted while this file was open would bring it back as the viewer's.
    // Replies that are feedback comments are the server's already.
    const repliesAreComments = this.repliesAreComments();
    const own = annotations.filter((annotation) => this.isEditable(annotation) && !(repliesAreComments && isReply(annotation)));
    if (this.lastSaveData && isEqual(own, this.lastSaveData)) {
      console.log('Not saving annotations, nothing changed since last save');
      this.annotationSavingState.set({ state: 'saved' });
      return of(void 0);
    }

    const prevSaveTime = this.lastSaveTime;
    this.lastSaveTime = new Date();
    this.annotationSavingState.set({ state: 'saving' });

    console.log('Saving annotations', this.lastSaveTime, own);

    return this.annotationService
      .saveAnnotations({
        target: currentTarget,
        isDraft: true,
        annotations: own,
        engineVersion: ENGINE_VERSION,
      })
      .pipe(
        tap({
          next: () => {
            this.lastSaveData = own;
            this.savesDone++;
            untracked(() => {
              this.initialAnnotations.set({ ...this.initialAnnotations.value(), draft: annotations });
            });
            this.annotationSavingState.set({
              state: 'saved',
            });
          },
          error: (error) => {
            this.lastSaveTime = prevSaveTime;
            this.annotationSavingState.set({
              state: 'failed',
              error: requestErrorToString(error),
            });
            this.setupSaveRetry(trigger);
          },
        }),
        map(() => void 0),
        catchError(() => of(void 0)),
      );
  }

  private setupSaveRetry(trigger: AnnotationSaveTrigger) {
    if (this.saveRetryTimer) return;

    this.secondsUntilRetry.set(this.SAVE_RETRY_WAIT_TIME);
    this.saveRetryTimer = setInterval(() => {
      const remaining = this.secondsUntilRetry();
      if (remaining <= 1) {
        this.cancelSaveRetry();
        this.annotationSaveTriggers$.next({
          type: 'retry',
          time: new Date(),
          originalTrigger: trigger,
        });
      } else {
        this.secondsUntilRetry.set(remaining - 1);
      }
    }, 1000);
  }

  private cancelSaveRetry() {
    clearInterval(this.saveRetryTimer);
    this.saveRetryTimer = null;
    this.secondsUntilRetry.set(undefined);
  }

  private getCurrentAnnotations(): object[] {
    const annotation: AnnotationCapability = this.pluginRegistry.value()?.getPlugin<AnnotationPlugin>(AnnotationPlugin.id)?.provides();
    if (!annotation) return [];
    return Object.values<TrackedAnnotation>(annotation.getState().byUid)
      .filter((x) => x.commitState !== 'deleted' && this.ANNOTATION_SUBTYPES_FOR_SAVE.includes(x.object.type))
      .map((x) => x.object);
  }

  protected retryLoading() {
    if (this.pdfUrl() && this.pdfDocument.error()) {
      this.pdfDocument.reload();
    }
    if (this.annotationTarget() && this.initialAnnotations.error()) {
      this.initialAnnotations.reload();
    }
  }

  protected readonly AlertSquareIcon = AlertSquareIcon;
}

export type PdfLoadingState =
  | { state: 'idle' }
  | {
      state: 'loading';
    }
  | {
      state: 'loaded';
    }
  | {
      state: 'failed';
      error: string;
    };

type AnnotationSaveTrigger =
  | {
      type: 'annotationEvent';
      event: AnnotationEvent;
      time: Date;
    }
  | {
      type: 'manualTrigger';
      time: Date;
    }
  | {
      type: 'retry';
      time: Date;
      originalTrigger: AnnotationSaveTrigger;
    };
export type AnnotationSavingState =
  | {
      state: 'unsaved';
    }
  | {
      state: 'saved';
    }
  | {
      state: 'saving';
    }
  | {
      state: 'failed';
      error: string;
    };
